import FeishuPanel from './FeishuPanel';
import WorkspaceMembersPanel from "./settings/WorkspaceMembersPanel";
import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, User, Check, SpinnerGap, Camera } from '@phosphor-icons/react';
import type { Profile } from '../types';
import * as api from '../api/client';
import McpTokensPanel from './settings/McpTokensPanel';
import MyMemoryPanel from './settings/MyMemoryPanel';
import MemoryProposalsPanel from './settings/MemoryProposalsPanel';
import WorkspaceAIPanel from './settings/WorkspaceAIPanel';

interface SettingsPageProps {
  /** Isolate the profile function for focused frontend previews. */
  profileOnly?: boolean;
  username: string;
  profile: Profile | null;
  workspaceId: string | null;
  workspaceName?: string;
  onBack: () => void;
  onProfileUpdate: (profile: Profile) => void;
}

const AVATAR_COLORS = [
  '#0033FF', '#6366f1', '#8b5cf6', '#a855f7',
  '#ec4899', '#ef4444', '#f97316', '#FF6B00',
  '#eab308', '#22c55e', '#16a34a', '#14b8a6',
  '#06b6d4', '#3b82f6', '#64748b', '#1e293b',
];

export default function SettingsPage({ profileOnly = false, username, profile, workspaceId, workspaceName, onBack, onProfileUpdate }: SettingsPageProps) {
  const [section,setSection]=useState<'personal'|'workspace'>(()=>!profileOnly && new URLSearchParams(window.location.search).get('section')==='workspace'?'workspace':'personal');
  const [displayName, setDisplayName] = useState(profile?.display_name || username);
  const [avatarColor, setAvatarColor] = useState(profile?.avatar_color || '#0033FF');
  const [avatarUrl, setAvatarUrl] = useState(profile?.avatar_url || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (profile) {
      setDisplayName(profile.display_name || username);
      setAvatarColor(profile.avatar_color || '#0033FF');
      setAvatarUrl(profile.avatar_url || '');
    }
  }, [profile, username]);

  const hasChanges = (displayName !== (profile?.display_name || username))
    || (avatarColor !== (profile?.avatar_color || '#0033FF'));

  const handleSave = async () => {
    if (saving || uploading) return;
    setSaving(true); setSaved(false); setSaveError('');
    try {
      const updated = await api.updateMyProfile({
        display_name: displayName,
        avatar_color: avatarColor,
      } as Partial<Profile>);
      onProfileUpdate(updated);
      setSaved(true);
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'Could not save your profile. Try again.');
    } finally { setSaving(false); }
  };

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      alert('Image must be under 2MB');
      return;
    }
    setUploading(true);
    try {
      const updated = await api.uploadAvatar(file);
      setAvatarUrl(updated.avatar_url || '');
      onProfileUpdate(updated);
    } catch (err: any) {
      alert(err.message || 'Upload failed');
    }
    setUploading(false);
    e.target.value = '';
  };

  const sectionStyle: React.CSSProperties = {
    background: 'var(--bg-surface)',
    border: '1px solid var(--border)',
    borderRadius: 12,
    padding: '20px 24px',
  };

  const labelStyle: React.CSSProperties = {
    fontSize: 'var(--fs-sm)',
    fontWeight: 600,
    color: 'var(--text-secondary)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: 8,
  };

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '8px 12px', fontSize: 'var(--fs-base)',
    border: '1px solid var(--border)', borderRadius: 8,
    background: 'var(--bg-primary)', color: 'var(--text-primary)',
    fontFamily: 'inherit', outline: 'none', transition: 'border-color 0.15s',
    boxSizing: 'border-box' as const,
  };

  const color = avatarColor || '#0033FF';

  return (
    <div className="coastal-settings" style={{ height: 'var(--app-height)', display: 'flex', flexDirection: 'column', background: 'var(--bg-primary)' }}>
      {/* Header */}
      <header className="coastal-settings-header" style={{
        height: 48, borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center',
        padding: '0 16px', flexShrink: 0, background: 'var(--bg-surface)', gap: 10,
      }}>
        <button
          onClick={onBack}
          aria-label="Back to workspace"
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 32, height: 32, borderRadius: 8,
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--text-muted)', transition: 'all 0.15s',
          }}
          onMouseOver={e => { e.currentTarget.style.background = 'var(--code-bg)'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
          onMouseOut={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = 'var(--text-muted)'; }}
        >
          <ArrowLeft size={15} weight="light" />
        </button>
        <span style={{ fontWeight: 600, fontSize: 'var(--fs-md)', color: 'var(--text-primary)' }}>Settings</span>
      </header>

      {/* Content */}
      <div className="coastal-settings-content" style={{ flex: 1, overflow: 'auto', padding: '32px 48px', maxWidth: 560, margin: '0 auto', width: '100%' }}>
        <div className="coastal-settings-layout" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

          {!profileOnly && <nav aria-label="Settings category" style={{display:'flex',gap:8}}>{(['personal','workspace'] as const).map(value=><button key={value} aria-pressed={section===value} onClick={()=>{setSection(value);window.history.replaceState(null,'',`/settings?section=${value}${workspaceId?`&workspace=${encodeURIComponent(workspaceId)}`:''}`);}} style={{padding:'8px 14px',border:'1px solid var(--border)',borderRadius:8,background:section===value?'var(--code-bg)':'transparent',color:'var(--text-primary)',cursor:'pointer'}}>{value==='personal'?'Personal settings':'Workspace settings'}</button>)}</nav>}
          {section==='personal'&&<section aria-label="Personal settings" id="personal-settings" style={{display:'flex',flexDirection:'column',gap:20}}>
          <div><h2 style={{margin:0,fontSize:20}}>Personal settings</h2><p style={{color:'var(--text-muted)',fontSize:13}}>{profileOnly?'Your name and avatar follow your account across workspaces.':'Your profile, connections, memory and access tokens. These follow your account across workspaces.'}</p></div>
          {/* Profile Section */}
          <div style={sectionStyle} aria-busy={saving || uploading}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              <User size={16} weight="light" style={{ color: 'var(--text-muted)' }} />
              <span style={{ fontSize: 'var(--fs-lg)', fontWeight: 600, color: 'var(--text-primary)' }}>Profile</span>
            </div>

            {/* Avatar + Info */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
              <div
                style={{ position: 'relative', cursor: profileOnly ? 'default' : 'pointer', flexShrink: 0 }}
                onClick={() => { if (!profileOnly) fileInputRef.current?.click(); }}
              >
                {avatarUrl ? (
                  <img
                    src={avatarUrl}
                    alt="Avatar"
                    style={{
                      width: 56, height: 56, borderRadius: '50%', objectFit: 'cover',
                      border: `2px solid ${color}`,
                    }}
                  />
                ) : (
                  <div style={{
                    width: 56, height: 56, borderRadius: '50%', background: color + '20',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 'var(--fs-xl)', fontWeight: 700, color,
                    border: `2px solid ${color}20`,
                    transition: 'all 0.2s',
                  }}>
                    {(displayName || '?')[0].toUpperCase()}
                  </div>
                )}
                {!profileOnly && <div style={{
                  position: 'absolute', inset: 0, borderRadius: '50%',
                  background: 'var(--ds-overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  opacity: 0, transition: 'opacity 0.15s',
                }}
                  onMouseOver={e => { e.currentTarget.style.opacity = '1'; }}
                  onMouseOut={e => { e.currentTarget.style.opacity = '0'; }}
                >
                  {uploading
                    ? <SpinnerGap size={18} weight="light" className="animate-spin" style={{ color: 'var(--ds-on-artwork)' }} />
                    : <Camera size={18} weight="light" style={{ color: 'var(--ds-on-artwork)' }} />
                  }
                </div>}
                <input disabled={profileOnly || saving || uploading}
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/gif,image/webp"
                  onChange={handleAvatarUpload}
                  style={{ display: 'none' }}
                />
              </div>
              <div>
                <div style={{ fontSize: 'var(--fs-lg)', fontWeight: 600, color: 'var(--text-primary)' }}>
                  {displayName || username}
                </div>
                <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-muted)' }}>
                  @{username}
                </div>
              </div>
            </div>

            {/* Display Name */}
            <div style={{ marginBottom: 16 }}>
              <label htmlFor="profile-display-name" style={{...labelStyle,display:'block'}}>Display Name</label>
              <input id="profile-display-name"
                readOnly={saving || uploading}
                value={displayName}
                onChange={e => { setDisplayName(e.target.value); setSaved(false); }}
                style={inputStyle}
                onFocus={e => { e.currentTarget.style.borderColor = 'var(--ds-accent)'; }}
                onBlur={e => { e.currentTarget.style.borderColor = 'var(--border)'; }}
              />
            </div>

            {/* Avatar Color */}
            <div>
              <div style={labelStyle}>Avatar Color</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {AVATAR_COLORS.map(c => (
                  <button
                    key={c}
                    aria-label={`Avatar color ${c}`} aria-pressed={avatarColor === c} disabled={saving || uploading}
                    onClick={() => { setAvatarColor(c); setSaved(false); }}
                    style={{
                      width: 28, height: 28, borderRadius: '50%', background: c,
                      border: avatarColor === c ? '2px solid var(--text-primary)' : '2px solid transparent',
                      cursor: 'pointer', transition: 'all 0.15s',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      outline: avatarColor === c ? '2px solid var(--bg-surface)' : 'none',
                      outlineOffset: -4,
                    }}
                    onMouseOver={e => { if (avatarColor !== c) e.currentTarget.style.transform = 'scale(1.15)'; }}
                    onMouseOut={e => { e.currentTarget.style.transform = 'scale(1)'; }}
                  >
                    {avatarColor === c && <Check size={14} weight="bold" style={{ color: 'var(--ds-on-artwork)' }} />}
                  </button>
                ))}
              </div>
            </div>

            {saveError && <p role="alert" style={{color:'var(--ds-danger)',fontSize:'var(--fs-sm)',marginTop:16}}>{saveError}</p>}
            {saved && <p role="status" style={{color:'var(--ds-success)',fontSize:'var(--fs-sm)',marginTop:16}}>Profile saved.</p>}
            {/* Save button */}
            {hasChanges && (
              <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  onClick={handleSave}
                  disabled={saving || uploading}
                  style={{
                    padding: '8px 20px', fontSize: 'var(--fs-sm)', fontWeight: 600,
                    background: 'var(--brand)', color: 'var(--text-on-brand)', border: 'none', borderRadius: 8,
                    cursor: saving ? 'default' : 'pointer', fontFamily: 'inherit',
                    display: 'flex', alignItems: 'center', gap: 6,
                    opacity: saving ? 0.7 : 1, transition: 'opacity 0.15s',
                  }}
                >
                  {saving ? <SpinnerGap size={14} weight="light" className="animate-spin" /> : null}
                  {saving ? 'Saving...' : saved ? 'Saved' : 'Save'}
                </button>
              </div>
            )}
          </div>

          {!profileOnly && <><div id="personal-feishu"><FeishuPanel settings /></div>

          {/* My Memory — user-scope notes the agent reads alongside
              the deck's task memory. Aligns PP main /dev branch
              feat/pp-consume-sdk MyMemoryPanel. */}
          {profile?.id && <MyMemoryPanel userId={profile.id} />}

          {/* Agent suggestions — HITL queue of memory writes agents
              proposed (parity gap B). Hidden when empty. */}
          <MemoryProposalsPanel />

          {/* MCP Personal Access Tokens — aligns PP main #135 */}
          <McpTokensPanel /></>}
          </section>}
          {section==='workspace'&&<section aria-label="Workspace settings" style={{display:'flex',flexDirection:'column',gap:20,marginTop:20}}>
            <div><h2 style={{margin:0,fontSize:20}}>Workspace settings</h2>{workspaceName&&<span className="coastal-workspace-context">{workspaceName}</span>}<p style={{color:'var(--text-muted)',fontSize:13}}>Shared model configuration and membership for the selected workspace. Managed by its owner.</p></div>
            <WorkspaceAIPanel workspaceId={workspaceId} />
            {workspaceId && <WorkspaceMembersPanel key={workspaceId} workspaceId={workspaceId} />}
          </section>}

        </div>
      </div>
    </div>
  );
}
